<script setup lang="ts">
const _props = defineProps<{
  data: LocationsProps
  chapterIndex?: number
  template?: 'page' | 'overlay'
}>()

const activeIndex = ref(-1)
const activeIndexDelayed = ref(activeIndex.value)

const { copiedIndex, copyText } = useCopyText()

const idle = {
  x: 50,
  y: 33,
}

const translate = computed(() => {
  if (!_props.data.map) return idle
  if (activeIndexDelayed.value === -1) return idle

  const activeItem = _props.data.map[activeIndexDelayed.value]

  return activeItem
    ? { x: activeItem.x, y: activeItem.y * (window.innerWidth >= 1024 ? 2 : 1) }
    : idle
})

const scale = computed(() => (activeIndexDelayed.value >= 0 ? 3 : 1.5))

watch(activeIndex, (_, old) => {
  activeIndexDelayed.value = -1

  setTimeout(
    () => {
      activeIndexDelayed.value = activeIndex.value
    },
    0,
    //old === -1 ? 0 : 1000,
  )
})
</script>

<template>
  <section
    :class="{
      'bg-white': template !== 'page' && template !== 'overlay' && data.theme !== 'light-grey',
      'bg-light-grey':
        template === 'page' || (template !== 'overlay' && data.theme === 'light-grey'),
    }"
  >
    <div
      v-if="data.trumpet || data.title"
      class="px-bleed relative z-1 flex flex-col items-center pt-25 pb-16 text-center lg:pt-40"
    >
      <ElementsChapter
        v-if="data.trumpet && chapterIndex && template !== 'overlay'"
        :chapter="data.trumpet"
        :index="chapterIndex"
      />

      <ElementsTitleRevealv2
        v-if="template !== 'overlay'"
        :title="data.title"
        class="font-display mt-6 max-w-[14em] text-3xl uppercase font-stretch-expanded lg:mt-7 lg:text-5xl"
      />

      <h3
        v-else
        class="font-display mt-6 max-w-[14em] text-3xl uppercase font-stretch-expanded lg:mt-7 lg:text-5xl"
      >
        {{ data.title }}
      </h3>
    </div>

    <div
      class="relative aspect-[1/1.8] overflow-clip"
      :class="template !== 'overlay' ? 'lg:aspect-video' : 'mx-bleed bg-black/15 lg:aspect-[10/7]'"
    >
      <div class="absolute left-0 h-full">
        <ul
          class="sticky z-1 flex flex-wrap gap-2 px-3 py-5 lg:px-5"
          :class="{
            'top-10': template !== 'overlay',
          }"
        >
          <li>
            <button
              :class="
                activeIndex === -1 ? 'text-neon-yellow bg-black' : 'bg-neon-yellow text-black'
              "
              class="border border-black/50 p-3 font-mono text-sm uppercase"
              @click="activeIndex = -1"
            >
              All
            </button>
          </li>
          <li v-for="(item, idx) in data.map" :key="idx">
            <button
              :class="
                activeIndex === idx ? 'text-neon-yellow bg-black' : 'bg-neon-yellow text-black'
              "
              class="border border-black/50 p-3 font-mono text-sm uppercase"
              @click="activeIndex = idx"
            >
              {{ item.title }}
            </button>
          </li>
        </ul>
      </div>

      <div class="relative flex h-full w-full items-center justify-center overflow-hidden">
        <div
          class="map-scale-container absolute inset-0 -top-1/2 left-[calc(-250vw_+_50%)] h-full w-[500vw] translate-x-(--translate-x) translate-y-(--translate-y) transition-transform duration-1000 ease-in-out max-lg:-top-[20vh] lg:translate-x-(--translate-x-lg) lg:translate-y-(--translate-y-lg)"
          :style="{
            '--scale': scale * 3,
            '--scale-lg': scale,
            '--translate-x': ((50 - translate.x) * (scale * 3)) / 5 + '%',
            '--translate-y': ((50 - translate.y) * (scale * 3)) / 5 + '%',
            '--translate-x-lg': ((50 - translate.x) * scale) / 5 + '%',
            '--translate-y-lg': ((50 - translate.y) * scale) / 5 + '%',
          }"
        >
          <LazyIconsMapMinified class="text-black/15" />

          <!-- markers -->
          <div
            v-for="(item, idx) in data.map"
            :key="idx"
            class="absolute top-(--top) left-(--left) z-2 -translate-1/2 transition-transform duration-200 ease-in-out lg:top-(--top-lg) lg:left-(--left-lg)"
            :class="activeIndex === idx ? 'scale-150' : 'scale-100'"
            :style="{
              '--left': item.x + '%',
              '--top': item.y * (template === 'overlay' ? 1.25 : 1.18) + '%',
              '--left-lg': item.x * (template === 'overlay' ? 1.002 : 1) + '%',
              '--top-lg': item.y * (template === 'overlay' ? 4.17 : 3.8) + '%',
            }"
          >
            <IconsLogomark class="size-6 lg:size-15" />
          </div>
        </div>
      </div>

      <ul class="absolute top-0 right-0 grid h-full max-w-[75%] items-end justify-end">
        <li
          v-for="(item, idx) in data.map"
          :key="idx"
          :class="
            activeIndex === idx
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none opacity-0'
          "
          class="group bg-neon-yellow sticky bottom-0 col-start-1 row-start-1 flex aspect-[4/5] w-full flex-col p-4 transition-opacity lg:aspect-[42/50] lg:p-6"
        >
          <p class="font-display pb-5 text-xl uppercase font-stretch-expanded">{{ item.title }}</p>

          <ElementsPortableTextWrapper :content="item.text" />

          <button
            type="button"
            aria-label="Copy text"
            class="relative z-10 cursor-pointer self-start opacity-0 transition-opacity group-hover:opacity-60 hover:!opacity-100"
            @click.stop="copyText(item.text, idx)"
          >
            <IconsCheck v-if="copiedIndex === idx" />
            <IconsCopy v-else />
          </button>

          <ElementsLogomarkLink
            v-for="(link, index) in item.callToAction"
            :key="index"
            class="mt-auto"
            :make-container-clickable="true"
            :link="link"
          />
        </li>
      </ul>
    </div>
  </section>
</template>

<style lang="postcss">
.map-scale-container {
  scale: calc(var(--scale) / 5) calc(var(--scale) / 5) 1;

  @media (width >= 64rem) {
    scale: calc(var(--scale-lg) / 5) calc(var(--scale-lg) / 5) 1;
  }
}
</style>
